import { KSpan, KTitleSpan } from "kahuna-base-react-components"
import { useMemo } from "react"
import { Doughnut } from "react-chartjs-2"
import type { ChartOptions } from "chart.js"
import { TrendDemographicStreamMap } from "./DemographicChart"
import { capitalize } from "../../../../utility"
import { lang } from "../../../../constants/languages"
import { colorPaletteOptions, GENDER_COLOR_MAP } from "../../../../constants"
import NotFound from "../../../NotFound"

type Props = {
  trendDemographicStreamMap: TrendDemographicStreamMap
  title?: string
}

//const DEFAULT_COLORS = ["#FFC107", "#17B73A", "#2D6BFF", "#DF0E88", "#FF4634"]

const GenderDoughnutChart = ({ trendDemographicStreamMap }: Props) => {
  const { labels, totals } = useMemo(() => {
    const entries = Object.entries(trendDemographicStreamMap || {})

    const computed = entries.map(([gender, ageMap]) => {
      const total = Object.values(ageMap || {}).reduce((sum, v) => sum + (Number(v) || 0), 0)
      return { gender, total }
    })

    const filtered = computed.filter((x) => x.total > 0)

    return {
      labels: filtered.map((x) => capitalize(x.gender)),
      totals: filtered.map((x) => x.total)
    }
  }, [trendDemographicStreamMap])

  const chartData = useMemo(() => {
    return {
      labels,
      datasets: [
        {
          data: totals,
          backgroundColor: labels.map(
            (key: string, i) => GENDER_COLOR_MAP[key as keyof typeof GENDER_COLOR_MAP] || colorPaletteOptions[i % 10]
          ),
          // hoverBackgroundColor: labels.map((key:string, i) => DEFAULT_COLORS[i % DEFAULT_COLORS.length]),
          borderWidth: 2,
          borderColor: "#FFFFFF"
          // borderAlign: "inner"
        }
      ]
    }
  }, [labels, totals])

  const options: ChartOptions<"doughnut"> = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      cutout: "70%",
      plugins: {
        legend: { display: false },
        tooltip: { enabled: false },

        // (chartjs-plugin-datalabels global register edilmişse bu şart)
        datalabels: {
          display: false
        }
      }
    }),
    []
  )

  const totalAll = useMemo(() => totals.reduce((s, v) => s + (Number(v) || 0), 0), [totals])
  const centerLegendPlugin = useMemo(() => {
    return {
      id: "centerLegend",
      afterDatasetsDraw(chart: any) {
        const { ctx } = chart
        const dataset = chart.data?.datasets?.[0]
        if (!dataset) return

        const data: number[] = (dataset.data || []).map((v: any) => Number(v) || 0)
        const labels: string[] = chart.data?.labels || []
        const colors: string[] = dataset.backgroundColor || []

        if (!data.length || !labels.length) return

        const total = data.reduce((s, v) => s + v, 0)
        if (total <= 0) return

        // ✅ build + filter
        const rows = labels
          .map((label, i) => {
            const value = data[i] || 0
            const pct = Math.round((value / total) * 100)
            return { label: String(label), pct, color: colors[i] || "#999", value }
          })
          .filter((r) => r.value > 0)
          // ✅ sort desc by value
          .sort((a, b) => b.value - a.value)

        if (!rows.length) return

        // ✅ Force total to 100 by correcting the last row (after sorting)
        if (rows.length === 1) {
          rows[0].pct = 100
        } else {
          const sumExceptLast = rows.slice(0, -1).reduce((s, r) => s + (r.pct || 0), 0)
          rows[rows.length - 1].pct = 100 - sumExceptLast
        }

        const meta = chart.getDatasetMeta(0)
        const arc = meta?.data?.[0]
        if (!arc) return

        const cx = arc.x
        const cy = arc.y

        const fontSize = 14
        const fontWeight = 600
        const boxSize = 14
        const lineHeight = 22

        const pctFontSize = 14
        const pctFontWeight = 700

        const labelFontSize = 12
        const labelFontWeight = 500

        const fontFamily = "Inter"
        const textColor = "#111"

        const blockHeight = rows.length * lineHeight
        let y = cy - blockHeight / 2 + lineHeight / 2

        ctx.save()
        ctx.textBaseline = "middle"
        ctx.textAlign = "left"
        ctx.font = `${fontWeight} ${fontSize}px ${fontFamily}`

        rows.forEach((r) => {
          const pctText = `${r.pct}%`
          const labelText = ` ${r.label}`

          ctx.save()
          ctx.font = `${pctFontWeight} ${pctFontSize}px ${fontFamily}`
          const pctWidth = ctx.measureText(pctText).width

          ctx.font = `${labelFontWeight} ${labelFontSize}px ${fontFamily}`
          const labelWidth = ctx.measureText(labelText).width
          ctx.restore()

          const rowWidth = boxSize + 8 + pctWidth + labelWidth
          const x = cx - rowWidth / 2

          // ✅ Color box
          ctx.fillStyle = r.color
          ctx.fillRect(x, y - boxSize / 2, boxSize, boxSize)

          // ✅ Percent (bold + bigger)
          ctx.fillStyle = textColor
          ctx.font = `${pctFontWeight} ${pctFontSize}px ${fontFamily}`
          ctx.fillText(pctText, x + boxSize + 8, y + 1)

          // ✅ Label (normal)
          ctx.font = `${labelFontWeight} ${labelFontSize}px ${fontFamily}`
          ctx.fillText(labelText, x + boxSize + 8 + pctWidth, y + 1)

          y += lineHeight
        })

        ctx.restore()
      }
    }
  }, [chartData])

  const sortedLegendItems = useMemo(() => {
    return labels.map((g, i) => ({ g, total: totals[i] ?? 0 })).sort((a, b) => (b.total ?? 0) - (a.total ?? 0))
  }, [labels, totals])

  return (
    <div
      style={{
        width: "100%",
        height: "100%",
        padding: 10,
        borderRadius: "10px",
        border: "1px solid #F3F3F3",
        background: "#FFF",
        boxShadow: "0px 2px 4px 0px rgba(27, 28, 29, 0.04)"
      }}
    >
      <div style={{ width: "100%", height: "5%", paddingLeft: 12, paddingTop: 4}}>
        <KTitleSpan text={lang.trends.headers.gender_breakdown} fontSize={16} color="#737373" lineHeight="20px" fontWeight={500} />
        {/*<KSpan text={`Total: ${totalAll}`} color="#737373" fontSize={12} />*/}
      </div>

      <div
      style={{ width: "100%", height: "95%", padding: "8px" }}
       className="flex flex-col gap-4 justify-center items-center mt-3">
        <div className="flex pt-5 w-full justify-center">
          {labels.length > 0 ? (
            <div className="w-[95%] max-w-[350px]">
              <Doughnut
                data={chartData}
                options={options}
                plugins={[centerLegendPlugin]}
                style={{
                  width: "100%",
                  aspectRatio: 1,
                  display: "flex",
                  justifyContent: "center"
                }}
              />
            </div>
          ) : (
            <div className="w-[70%]  max-w-[320px]">
              <NotFound title={lang.not_found.trends.title} description={lang.not_found.trends.desc} imageWidth={400} />
            </div>
          )}
        </div>
        <div className="mt-2 w-full flex flex-wrap gap-3 justify-center">
          {labels.length > 0 &&
            sortedLegendItems.map(({ g, total }, i) => (
              <div key={g} className="flex items-center gap-2">
                <div
                  className="w-2.5 h-2.5 rounded-full"
                  style={{ backgroundColor: GENDER_COLOR_MAP[g as keyof typeof GENDER_COLOR_MAP] || colorPaletteOptions[i % 10] }}
                />
                <KSpan text={`${g}: ${total}`} color="#111" fontSize={12} />
              </div>
            ))}
        </div>
      </div>
    </div>
  )
}

export default GenderDoughnutChart
